import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen, Search, FileText, Eye, ThumbsUp,
  Plus, ChevronRight, Filter, ExternalLink, Globe, Building2,
} from 'lucide-react';
import { kbApi, type KBCollection } from '../../../api/kb';

// ─── Status helpers ─────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-amber-100 text-amber-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-neutral-100 text-neutral-500',
  review: 'bg-blue-100 text-blue-700',
  scheduled: 'bg-violet-100 text-violet-700',
};

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function KnowledgeBasePage() {
  const { wsSlug } = useParams<{ wsSlug: string }>();
  const navigate = useNavigate();
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  // Collections sidebar
  const { data: collectionsData } = useQuery({
    queryKey: ['kb-collections'],
    queryFn: () => kbApi.listCollections(),
    staleTime: 60_000,
  });
  const collections = collectionsData?.collections ?? [];

  // Articles list
  const { data: articlesData, isLoading: articlesLoading, isError } = useQuery({
    queryKey: ['kb-articles', selectedCollection, statusFilter, search],
    queryFn: () => kbApi.listArticles({
      collection_id: selectedCollection ?? undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: search || undefined,
    }),
    staleTime: 30_000,
  });
  const articles = articlesData?.articles ?? [];

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BookOpen size={20} className="text-indigo-600" />
              <h1 className="text-xl font-semibold text-neutral-900">Knowledge Base</h1>
            </div>
            <p className="text-sm text-neutral-500">Documentation and self-service resources</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/w/${wsSlug}/su/kb/new`)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <Plus size={13} /> New Article
            </button>
            <a
              href={`/w/${wsSlug}/sm/kb`}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-500 hover:text-neutral-700 border border-neutral-200 rounded-lg bg-white hover:bg-neutral-50 transition-colors"
            >
              <ExternalLink size={12} />
              Open in Content Editor
            </a>
          </div>
        </div>

        {/* Body: sidebar + article list */}
        <div className="flex gap-6">
          {/* Collections sidebar */}
          <div className="w-48 shrink-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 mb-2">Collections</p>
            <nav className="space-y-0.5">
              <button
                onClick={() => setSelectedCollection(null)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedCollection === null
                    ? 'bg-indigo-50 text-indigo-700 font-medium'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
              >
                All Articles
              </button>
              {collections.map((col: KBCollection) => (
                <button
                  key={col.id}
                  onClick={() => setSelectedCollection(col.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedCollection === col.id
                      ? 'bg-indigo-50 text-indigo-700 font-medium'
                      : 'text-neutral-600 hover:bg-neutral-100'
                  }`}
                >
                  {col.name}
                  {col.article_count !== undefined && (
                    <span className="ml-1.5 text-[10px] text-neutral-400">({col.article_count})</span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Main content — Article list */}
          <div className="flex-1 min-w-0">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Filter size={14} className="text-neutral-400" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white text-neutral-700">
                  <option value="all">All statuses</option>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            {/* Article list */}
            <div className="bg-white border border-neutral-100 rounded-xl shadow-sm">
              {articlesLoading ? (
                <div className="p-6 space-y-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="h-4 w-8 bg-neutral-100 rounded animate-pulse" />
                      <div className="h-4 flex-1 bg-neutral-100 rounded animate-pulse" />
                      <div className="h-4 w-16 bg-neutral-100 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : isError ? (
                <div className="p-10 text-center text-sm text-red-600">
                  Failed to load articles. Check that the KB service is running.
                </div>
              ) : articles.length === 0 ? (
                <div className="p-10 text-center">
                  <FileText size={32} className="mx-auto text-neutral-300 mb-2" />
                  <p className="text-sm text-neutral-400">
                    {search ? 'No articles match your search' : 'No articles in this collection'}
                  </p>
                  <button
                    onClick={() => navigate(`/w/${wsSlug}/su/kb/new`)}
                    className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                  >
                    <Plus size={14} /> Create your first article
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-neutral-50">
                  {articles.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => navigate(`/w/${wsSlug}/su/kb/${article.id}/edit`)}
                      className="w-full flex items-center gap-4 px-5 py-3 hover:bg-neutral-50 transition-colors text-left"
                    >
                      <FileText size={16} className="text-neutral-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 truncate">{article.title}</p>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-neutral-400">
                            {new Date(article.updated_at).toLocaleDateString()}
                          </span>
                          {article.site && (
                            <span className="flex items-center gap-0.5 text-xs text-neutral-400">
                              <Globe size={10} /> {article.site}
                            </span>
                          )}
                          {article.views_count !== undefined && (
                            <span className="flex items-center gap-0.5 text-xs text-neutral-400">
                              <Eye size={10} /> {article.views_count}
                            </span>
                          )}
                          {article.helpfulness_avg !== undefined && article.helpfulness_avg > 0 && (
                            <span className="flex items-center gap-0.5 text-xs text-neutral-400">
                              <ThumbsUp size={10} /> {article.helpfulness_avg.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[article.status] ?? 'bg-neutral-100 text-neutral-500'}`}>
                        {article.status}
                      </span>
                      {article.org_name && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                          <Building2 size={10} />
                          {article.org_name}
                        </span>
                      )}
                      <ChevronRight size={16} className="text-neutral-300 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
